import { motion } from 'framer-motion'
import { Briefcase, Megaphone, Workflow, Globe } from 'lucide-react'
import { Link } from 'react-router'
import { workers as allWorkers } from '@/constants/workers'

type Worker = {
    name: string
    role: string
    department: string
    capabilities: string[]
    icon: React.ComponentType<{ className?: string }>
    image: string
    accent: string // tailwind text color class
}

const workers: Worker[] = [
    {
        name: 'Atlas',
        role: 'AI Sales Development',
        department: 'Sales',
        capabilities: [
            'Sources prospects from millions of signals',
            'Qualifies against your ICP automatically',
            'Runs multi-channel outreach: email, LinkedIn, DMs',
        ],
        icon: Briefcase,
        image: '/platform_screenshots/prospects-kanban.jpg',
        accent: 'text-emerald-400',
    },
    {
        name: 'Echo',
        role: 'Content Producer',
        department: 'Marketing',
        capabilities: [
            'Generates posts, images, podcasts, music',
            'Publishes on schedule across every channel',
            'Auto-responds to comments and DMs',
        ],
        icon: Megaphone,
        image: '/platform_screenshots/creative-studio.jpg',
        accent: 'text-fuchsia-400',
    },
    {
        name: 'Forge',
        role: 'Operations Manager',
        department: 'Operations',
        capabilities: [
            'Runs your workflows on cron, no babysitting',
            'Tracks KPIs in real time and flags anomalies',
            'Routes high-impact actions to you for approval',
        ],
        icon: Workflow,
        image: '/platform_screenshots/operations-overview.jpg',
        accent: 'text-sky-400',
    },
    {
        name: 'Rune',
        role: 'Browser Worker',
        department: 'Platform',
        capabilities: [
            'Browses the web autonomously, end-to-end',
            'Fills forms, scrapes data, audits sites',
            'Live co-pilot mode when you need to take over',
        ],
        icon: Globe,
        image: '/platform_screenshots/browser-automation.jpg',
        accent: 'text-amber-400',
    },
]

export function MeetWorkers() {
    return (
        <section
            id="meet-workers"
            className="py-20 px-6 md:px-12 lg:px-16"
        >
            <div className="w-full max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center mb-14"
                >
                    <span className="inline-block font-body text-xs uppercase tracking-widest text-muted-foreground border border-border rounded-full px-4 py-1.5 mb-6">
                        Meet your workers
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                        Hire by role.<br />Pay by output.
                    </h2>
                    <p className="font-body text-lg text-muted-foreground font-light max-w-2xl mx-auto">
                        Each worker has a name, a job, and a track record. They run autonomously across your stack — and you only pay when they ship.
                    </p>
                </motion.div>

                {/* Worker grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {workers.map((worker, index) => (
                        <WorkerCard key={worker.name} worker={worker} index={index} />
                    ))}
                </div>

                {/* CTA to full roster */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-center mt-12"
                >
                    <Link
                        to="/workers"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border bg-card hover:bg-accent text-foreground font-body text-sm transition-colors"
                    >
                        Browse all {allWorkers.length}+ workers
                        <span aria-hidden="true">→</span>
                    </Link>
                </motion.div>
            </div>
        </section>
    )
}

function WorkerCard({ worker, index }: { worker: Worker; index: number }) {
    const Icon = worker.icon
    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: (index % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="group overflow-hidden rounded-2xl border border-border bg-card hover:border-primary/40 transition-colors duration-300 flex flex-col"
        >
            {/* Top: identity row */}
            <div className="flex items-start gap-4 p-6 pb-4">
                <div className={`w-12 h-12 rounded-xl border border-border bg-background flex items-center justify-center shrink-0 ${worker.accent}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <h3 className="font-heading text-2xl text-foreground">{worker.name}</h3>
                        <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                            {worker.department}
                        </span>
                    </div>
                    <p className="font-body text-base text-muted-foreground font-light">{worker.role}</p>
                </div>
            </div>

            {/* Capabilities */}
            <ul className="px-6 pb-5 space-y-2">
                {worker.capabilities.map((cap) => (
                    <li
                        key={cap}
                        className="flex items-start gap-3 font-body text-sm text-muted-foreground font-light"
                    >
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-current ${worker.accent}`} />
                        <span>{cap}</span>
                    </li>
                ))}
            </ul>

            {/* In-action screenshot */}
            <div className="relative mt-auto h-56 overflow-hidden border-t border-border">
                <motion.img
                    src={worker.image}
                    alt={`${worker.name} in action`}
                    initial={{ scale: 1.08 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full h-full object-cover object-top"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
            </div>
        </motion.article>
    )
}
